import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Award, Users, Building, Target, Eye, Heart } from 'lucide-react';

const About = () => {
  const teamMembers = [
    {
      name: 'Shubham Pratap Singh',
      role: 'Principal Architect',
      image: '/images/1.jpeg?auto=compress&cs=tinysrgb&w=400',
      description: '15+ years of experience in residential and commercial architecture'
    },
    {
      name: 'Kunj Pratap Singh',
      role: 'Senior Designer',
      image: '/images/4.jpeg?auto=compress&cs=tinysrgb&w=400',
      description: 'Specializes in interior design and space planning'
    },
    {
      name: 'Shubham Pratap',
      role: 'Project Manager',
      image: '/images/14.jpeg?auto=compress&cs=tinysrgb&w=400',
      description: 'Expert in construction management and client relations'
    }
  ];

  const milestones = [
    { year: '2008', event: 'Company Founded', description: 'Started with a vision to create exceptional architectural solutions' },
    { year: '2012', event: '100+ Projects', description: 'Completed our first 100 successful projects' },
    { year: '2016', event: 'Award Recognition', description: 'Received State Architecture Excellence Award' },
    { year: '2020', event: '500+ Clients', description: 'Reached milestone of serving 500+ satisfied clients' },
    { year: '2024', event: 'Expansion', description: 'Expanded services to include sustainable design solutions' }
  ];

  return (
    <>
      <Helmet>
        <title>About Us - Braj Decor And Designers | Professional Architecture Team</title>
        <meta name="description" content="Learn about our experienced architecture team, company history, and commitment to excellence in design and construction." />
        <meta name="keywords" content="architecture team, company history, professional architects, design excellence" />
      </Helmet>

     <div className="pt-0">
  {/* Breadcrumb / Banner Section */}
 {/* Breadcrumb Section */}
       <div
  className="relative w-full h-64 bg-cover bg-center bg-[url('https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1600&q=80')]"
>

         <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
           <motion.h2
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.7 }}
             className="text-4xl md:text-5xl font-bold text-yellow-400 drop-shadow-md"
           >
             About Us
           </motion.h2>
           <motion.p
             initial={{ opacity: 0 }}
             animate={{ opacity: 1 }}
             transition={{ delay: 0.4 }}
             className="mt-2 text-white font-medium"
           >
             Home &gt; <span className="text-yellow-400">About Us</span>
           </motion.p>
         </div>
       </div>
{/* Hero Section */}
{/* Company Introduction */}
<section className="py-20 bg-white">
  <div className="container mx-auto px-4">
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="text-4xl font-bold text-gray-900 mb-6">
          PVC Panel Manufacturer, Wholesaler & Interior Design Experts
        </h2>

        <p className="text-lg text-gray-600 mb-6 leading-relaxed">
          Braj Decor and Designers was established in 2008 under the leadership of 
          Mr. Shubham Pratap Singh. With over a decade of experience, we have become 
          a trusted name in PVC panel manufacturing, wholesale supply, and complete 
          interior solutions for residential and commercial projects.
        </p>

        <p className="text-lg text-gray-600 mb-10 leading-relaxed">
          By combining premium-quality materials, modern technology, and creative 
          design expertise, we deliver interiors that are stylish, durable, and 
          highly functional.
        </p>

        <div className="grid grid-cols-2 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600 mb-2">500+</div>
            <div className="text-gray-600">Projects Completed</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600 mb-2">15+</div>
            <div className="text-gray-600">Years Experience</div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="relative"
      >
        <img
          src="/images/1.jpeg?auto=compress&cs=tinysrgb&w=800"
          alt="Our Team at Work"
          className="rounded-lg shadow-2xl"
        />
        <div className="absolute -bottom-6 -left-6 bg-yellow-400 text-white p-6 rounded-lg">
          <Award className="h-8 w-8 mb-2" />
          <div className="font-bold">Since 2008</div>
          <div className="text-sm">Trusted Interior Brand</div>
        </div>
      </motion.div>
    </div>
  </div>
</section>

{/* ===== Kitchen Interior Section (2 Columns) ===== */}
<section className="py-16 bg-white">
  <div className="container mx-auto px-4">
    <div className="grid md:grid-cols-2 gap-12 items-start">
      {/* Left Column */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
      >
        <h3 className="text-3xl font-bold text-gray-900 mb-4">
          Kitchen Interior Work
        </h3>
        <p className="text-lg text-gray-600 leading-relaxed">
          We provide modular and customized kitchen interior solutions designed
          for comfort, beauty, and maximum utility. Our kitchens are planned with
          modern aesthetics, smart storage, and durable materials.
        </p>
      </motion.div>

      {/* Right Column */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
      >
        <p className="text-gray-600 mb-2">• Modular Kitchen Design</p>
        <p className="text-gray-600 mb-2">• PVC & Wooden Finish Cabinets</p>
        <p className="text-gray-600 mb-2">• Smart Storage Solutions</p>
        <p className="text-gray-600 mb-2">• Waterproof & Termite Resistant Materials</p>
        <p className="text-gray-600">• Easy to Clean & Maintain</p>
      </motion.div>
    </div>
  </div>
</section>

{/* ===== 2D & 3D Designing Section (2 Columns) ===== */}
<section className="py-16 bg-gray-50">
  <div className="container mx-auto px-4">
    <div className="grid md:grid-cols-2 gap-12 items-start">
      {/* Left Column */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
      >
        <h3 className="text-3xl font-bold text-gray-900 mb-4">
          2D & 3D Interior Designing
        </h3>
        <p className="text-lg text-gray-600 leading-relaxed">
          Our professional 2D and 3D design services allow clients to clearly
          visualize layouts, materials, colors, and lighting before execution,
          ensuring perfect results.
        </p>
      </motion.div>

      {/* Right Column */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
      >
        <p className="text-gray-600 mb-2">• 2D Floor Plans</p>
        <p className="text-gray-600 mb-2">• Layout Planning</p>
        <p className="text-gray-600 mb-2">• Space Management</p>
        <p className="text-gray-600 mb-2">• Realistic 3D Views</p>
        <p className="text-gray-600 mb-2">• Color & Material Visualization</p>
        <p className="text-gray-600">• Lighting & Furniture Concepts</p>
      </motion.div>
    </div>
  </div>
</section>

        {/* Mission, Vision, Values */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Core Values</h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                  <Target className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Mission</h3>
                <p className="text-gray-600 leading-relaxed">
                  To deliver high-quality products and innovative interior solutions that exceed customer expectations.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white p-8 rounded-xl shadow-lg text-center"
              >
                <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                  <Eye className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Vision</h3>
                <p className="text-gray-600 leading-relaxed">
                  To become a leading name in the PVC panel and interior design industry through quality, creativity, and trust.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white p-8 rounded-xl shadow-lg text-center"
              >
                <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                  <Heart className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Values</h3>
                <p className="text-gray-600 leading-relaxed">
                  Integrity, excellence, innovation, and client-centricity form the foundation of 
                  everything we do, ensuring lasting relationships and outstanding results.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Meet Our Team</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our talented team of architects, designers, and project managers brings years of experience 
                and passion to every project.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {teamMembers.map((member, index) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                    <p className="text-yellow-600 font-semibold mb-3">{member.role}</p>
                    <p className="text-gray-600">{member.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-20 bg-gray-900 text-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-6">Our Journey</h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Key milestones in our growth and success story
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className={`flex items-center mb-12 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className="flex-1 px-8">
                    <div className={`${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                      <div className="text-2xl font-bold text-yellow-400 mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-semibold mb-2">{milestone.event}</h3>
                      <p className="text-gray-300">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 bg-yellow-400 rounded-full flex-shrink-0 relative">
                    <div className="absolute w-px h-12 bg-gray-600 top-4 left-1/2 transform -translate-x-1/2"></div>
                  </div>
                  <div className="flex-1"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;